export const DEFAULT_REDEEM_CODE_STATUS_FILTERS = Object.freeze([
  "CLAIMED",
  "UNCLAIMED",
]);

const STATUS_CLASS_MAP = {
  CLAIMED:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  UNCLAIMED: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  CANCELLED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  CANCELED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  EXPIRED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  REVOKED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  TRANSFER:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
};

const toStatusArray = (statuses) => {
  if (Array.isArray(statuses)) return statuses;
  if (statuses) return [statuses];

  return [];
};

export const normalizeRedeemCodeStatus = (status) =>
  String(status || "")
    .trim()
    .toUpperCase();

export const formatRedeemCodeStatus = (status) => {
  const normalized = normalizeRedeemCodeStatus(status);

  if (!normalized) return "Unknown";

  return normalized
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b[a-z]/g, (char) => char.toUpperCase());
};

export const getRedeemCodeStatusClass = (status) =>
  STATUS_CLASS_MAP[normalizeRedeemCodeStatus(status)] ||
  "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";

export const getRedeemCodeStatusOptions = (redeemCodes = []) => {
  const countsByStatus = new Map(
    DEFAULT_REDEEM_CODE_STATUS_FILTERS.map((status) => [status, 0])
  );

  for (const code of redeemCodes || []) {
    const status = normalizeRedeemCodeStatus(code?.status);

    if (!status) continue;
    countsByStatus.set(status, (countsByStatus.get(status) || 0) + 1);
  }

  const priority = new Map(
    DEFAULT_REDEEM_CODE_STATUS_FILTERS.map((status, index) => [status, index])
  );

  return Array.from(countsByStatus.entries())
    .map(([value, count]) => ({
      count,
      label: formatRedeemCodeStatus(value),
      value,
    }))
    .sort((a, b) => {
      const aPriority = priority.has(a.value)
        ? priority.get(a.value)
        : DEFAULT_REDEEM_CODE_STATUS_FILTERS.length;
      const bPriority = priority.has(b.value)
        ? priority.get(b.value)
        : DEFAULT_REDEEM_CODE_STATUS_FILTERS.length;

      if (aPriority !== bPriority) return aPriority - bPriority;

      return a.label.localeCompare(b.label);
    });
};

export const filterRedeemCodesByStatuses = (
  redeemCodes = [],
  selectedStatuses = DEFAULT_REDEEM_CODE_STATUS_FILTERS
) => {
  const selected = new Set(
    toStatusArray(selectedStatuses)
      .map(normalizeRedeemCodeStatus)
      .filter(Boolean)
  );

  if (selected.size === 0) return [];

  return (redeemCodes || []).filter((code) =>
    selected.has(normalizeRedeemCodeStatus(code?.status))
  );
};

export const getRedeemCodeStatusFilterLabel = (
  selectedStatuses = DEFAULT_REDEEM_CODE_STATUS_FILTERS,
  options = []
) => {
  const selected = new Set(
    toStatusArray(selectedStatuses)
      .map(normalizeRedeemCodeStatus)
      .filter(Boolean)
  );

  if (selected.size === 0) return "None";

  const selectedOptions = (options || []).filter((option) =>
    selected.has(normalizeRedeemCodeStatus(option?.value))
  );

  if (selectedOptions.length === 0) return "None";

  const defaultSelected = DEFAULT_REDEEM_CODE_STATUS_FILTERS.every((status) =>
    selected.has(status)
  );

  if (defaultSelected && selectedOptions.length === 2) {
    return "Claimed + Unclaimed";
  }

  if (selectedOptions.length === options.length) return "All";
  if (selectedOptions.length === 1) return selectedOptions[0].label;

  return `${selectedOptions.length} selected`;
};
